import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Calendar, Users, Plane, Euro, Send, CheckCircle, AlertCircle, Loader2, Info } from 'lucide-react';
import Header from '../components/Header';
import { useLanguage } from '../context/LanguageContext';

const BookingRequest = () => {
  const { t, language } = useLanguage();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    country: '',
    checkIn: '',
    checkOut: '',
    adults: 2,
    children: 0,
    airportTransfer: 'none',
    proposedPriceEUR: '',
    proposedPriceUSD: '',
    proposedPriceTHB: '',
    message: ''
  });
  
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [nights, setNights] = useState(0);
  const [enteredCurrency, setEnteredCurrency] = useState(null);

  // Exchange rates (approximate - December 2025)
  const exchangeRates = {
    EUR_USD: 1.17,
    EUR_THB: 37,
    USD_THB: 31.2
  };

  // Handle currency conversion
  const handlePriceChange = (currency, value) => {
    const numValue = value === '' ? '' : parseFloat(value);
    
    if (value === '' || isNaN(numValue)) {
      setFormData(prev => ({
        ...prev,
        proposedPriceEUR: '',
        proposedPriceUSD: '',
        proposedPriceTHB: ''
      }));
      setEnteredCurrency(null);
      return;
    }

    setEnteredCurrency(currency);
    let eurValue, usdValue, thbValue;
    
    if (currency === 'EUR') {
      eurValue = String(numValue);
      usdValue = (numValue * exchangeRates.EUR_USD).toFixed(2);
      thbValue = (numValue * exchangeRates.EUR_THB).toFixed(2);
    } else if (currency === 'USD') {
      usdValue = String(numValue);
      eurValue = (numValue / exchangeRates.EUR_USD).toFixed(2);
      thbValue = (numValue * exchangeRates.USD_THB).toFixed(2);
    } else if (currency === 'THB') {
      thbValue = String(numValue);
      eurValue = (numValue / exchangeRates.EUR_THB).toFixed(2);
      usdValue = (numValue / exchangeRates.USD_THB).toFixed(2);
    }

    setFormData(prev => ({
      ...prev,
      proposedPriceEUR: eurValue,
      proposedPriceUSD: usdValue,
      proposedPriceTHB: thbValue
    }));
  };

  // Calculate nights when dates change
  useEffect(() => {
    if (formData.checkIn && formData.checkOut) {
      const start = new Date(formData.checkIn);
      const end = new Date(formData.checkOut);
      const diffTime = end - start;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      setNights(diffDays > 0 ? diffDays : 0);
    } else {
      setNights(0);
    }
  }, [formData.checkIn, formData.checkOut]);

  // Translations
  const texts = {
    de: {
      title: 'Buchung anfragen',
      subtitle: 'Kostenlose und unverbindliche Anfrage',
      infoText: 'Sie haben bei uns die Möglichkeit, einen Mietpreis für den gesamten Zeitraum vorzuschlagen. Dies ist natürlich vollkommen unverbindlich.',
      travelDates: 'Reisedaten',
      minStay: '(Mindestaufenthalt 3 Nächte)',
      minStayError: 'Der Mindestaufenthalt beträgt 3 Nächte.',
      checkIn: 'Anreise',
      checkOut: 'Abreise',
      nights: 'Nächte',
      guests: 'Gäste',
      adults: 'Erwachsene',
      children: 'Kinder (bis 16 Jahre)',
      maxGuests: 'Maximal 6 Personen',
      priceRequired: 'Bitte geben Sie einen Preisvorschlag ein.',
      contactData: 'Ihre Kontaktdaten',
      name: 'Name',
      email: 'E-Mail',
      phone: 'Telefon (optional)',
      country: 'Herkunftsland',
      countryPlaceholder: 'z.B. Deutschland',
      airportTransfer: 'Flughafentransfer',
      transferNo: 'Nein',
      transferYes: 'Ja',
      transferInfo: 'Transfer vom Flughafen Bangkok (Suvarnabhumi) zur Villa Thailand. Sie werden in der Ankunftshalle auf Level 2 direkt nach der Passkontrolle abgeholt.',
      priceProposal: 'Ihr Preisvorschlag',
      proposedPrice: 'Vorgeschlagener Gesamtpreis (EUR)',
      pricePlaceholder: 'z.B. 1200',
      priceInfo: 'Schlagen Sie einen Preis',
      priceInfoBold: 'für den gesamten Aufenthalt',
      priceInfoEnd: 'vor. Dieser ist erst nach unserer Bestätigung verbindlich.',
      priceConversionNote: 'Die Umrechnungskurse sind Richtwerte. Der tagaktuelle Kurs wird berücksichtigt.',
      correspondsTo: 'entspricht ca.',
      additionalMessage: 'Zusätzliche Nachricht (optional)',
      messagePlaceholder: 'Besondere Wünsche oder Fragen...',
      submitButton: 'Unverbindliche Anfrage senden',
      submitting: 'Wird gesendet...',
      successMessage: 'Ihre Anfrage wurde erfolgreich gesendet! Sie erhalten in Kürze eine Bestätigung per E-Mail.',
      errorMessage: 'Es gab einen Fehler. Bitte versuchen Sie es später erneut.',
      errorSmtpAuth: 'E-Mail-Server-Authentifizierung fehlgeschlagen. Bitte kontaktieren Sie uns direkt.',
      errorSmtpConnect: 'Verbindung zum E-Mail-Server fehlgeschlagen. Bitte versuchen Sie es später erneut.',
      errorSmtpTimeout: 'E-Mail-Server-Timeout. Bitte versuchen Sie es später erneut.',
      errorInvalidEmail: 'Die eingegebene E-Mail-Adresse ist ungültig.',
      errorEmailConfig: 'E-Mail-Konfiguration fehlt. Bitte kontaktieren Sie uns direkt unter service@villa-thailand.com',
      namePlaceholder: 'Ihr vollständiger Name',
      emailPlaceholder: 'ihre@email.de',
      phonePlaceholder: 'Ihre Telefonnummer'
    },
    en: {
      title: 'Request Booking',
      subtitle: 'Free and non-binding inquiry',
      infoText: 'You have the opportunity to propose a rental price for the entire period. This is of course completely non-binding.',
      travelDates: 'Travel Dates',
      minStay: '(Minimum stay 3 nights)',
      minStayError: 'The minimum stay is 3 nights.',
      checkIn: 'Check-in',
      checkOut: 'Check-out',
      nights: 'Nights',
      guests: 'Guests',
      adults: 'Adults',
      children: 'Children (up to 16 years)',
      maxGuests: 'Maximum 6 guests',
      priceRequired: 'Please enter a price proposal.',
      contactData: 'Your Contact Details',
      name: 'Name',
      email: 'Email',
      phone: 'Phone (optional)',
      country: 'Country of Origin',
      countryPlaceholder: 'e.g. Germany',
      airportTransfer: 'Airport Transfer',
      transferNo: 'No',
      transferYes: 'Yes',
      transferInfo: 'Transfer from Bangkok Airport (Suvarnabhumi) to Villa Thailand. You will be picked up in the arrival hall on Level 2 directly after passport control.',
      priceProposal: 'Your Price Proposal',
      proposedPrice: 'Proposed Total Price (EUR)',
      pricePlaceholder: 'e.g. 1200',
      priceInfo: 'Propose a price',
      priceInfoBold: 'for your entire stay',
      priceInfoEnd: '. This will only be binding after our confirmation.',
      priceConversionNote: 'Exchange rates are approximate. The current daily rate will be applied.',
      correspondsTo: 'corresponds to approx.',
      additionalMessage: 'Additional Message (optional)',
      messagePlaceholder: 'Special requests or questions...',
      submitButton: 'Send Non-binding Inquiry',
      submitting: 'Sending...',
      successMessage: 'Your inquiry has been sent successfully! You will receive a confirmation email shortly.',
      errorMessage: 'There was an error. Please try again later.',
      errorSmtpAuth: 'Email server authentication failed. Please contact us directly.',
      errorSmtpConnect: 'Connection to email server failed. Please try again later.',
      errorSmtpTimeout: 'Email server timeout. Please try again later.',
      errorInvalidEmail: 'The entered email address is invalid.',
      errorEmailConfig: 'Email configuration missing. Please contact us directly at service@villa-thailand.com',
      namePlaceholder: 'Your full name',
      emailPlaceholder: 'your@email.com',
      phonePlaceholder: 'Your phone number'
    },
    th: {
      title: 'ขอจอง',
      subtitle: 'การสอบถามฟรีและไม่มีข้อผูกมัด',
      infoText: 'คุณมีโอกาสเสนอราคาค่าเช่าสำหรับทั้งช่วงเวลา แน่นอนว่าสิ่งนี้ไม่มีข้อผูกมัดใดๆ',
      travelDates: 'วันที่เดินทาง',
      minStay: '(พักขั้นต่ำ 3 คืน)',
      minStayError: 'ระยะเวลาพักขั้นต่ำคือ 3 คืน',
      checkIn: 'เช็คอิน',
      checkOut: 'เช็คเอาท์',
      nights: 'คืน',
      guests: 'ผู้เข้าพัก',
      adults: 'ผู้ใหญ่',
      children: 'เด็ก (อายุไม่เกิน 16 ปี)',
      maxGuests: 'สูงสุด 6 คน',
      priceRequired: 'กรุณาใส่ข้อเสนอราคา',
      contactData: 'ข้อมูลติดต่อของคุณ',
      name: 'ชื่อ',
      email: 'อีเมล',
      phone: 'โทรศัพท์ (ไม่บังคับ)',
      country: 'ประเทศต้นทาง',
      countryPlaceholder: 'เช่น เยอรมนี',
      airportTransfer: 'บริการรับส่งสนามบิน',
      transferNo: 'ไม่',
      transferYes: 'ใช่',
      transferInfo: 'รับส่งจากสนามบินกรุงเทพ (สุวรรณภูมิ) ไป Villa Thailand คุณจะถูกรับที่ห้องโถงผู้โดยสารขาเข้า ชั้น 2 หลังจากผ่านตรวจคนเข้าเมือง',
      priceProposal: 'ราคาที่คุณเสนอ',
      proposedPrice: 'ราคารวมที่เสนอ (EUR)',
      pricePlaceholder: 'เช่น 1200',
      priceInfo: 'เสนอราคา',
      priceInfoBold: 'สำหรับการเข้าพักทั้งหมด',
      priceInfoEnd: ' จะมีผลผูกพันหลังจากเรายืนยันเท่านั้น',
      priceConversionNote: 'อัตราแลกเปลี่ยนเป็นค่าประมาณ จะใช้อัตราปัจจุบันในวันที่ทำรายการ',
      correspondsTo: 'เทียบเท่าประมาณ',
      additionalMessage: 'ข้อความเพิ่มเติม (ไม่บังคับ)',
      messagePlaceholder: 'คำขอพิเศษหรือคำถาม...',
      submitButton: 'ส่งการสอบถามที่ไม่มีข้อผูกมัด',
      submitting: 'กำลังส่ง...',
      successMessage: 'ส่งคำขอของคุณเรียบร้อยแล้ว! คุณจะได้รับอีเมลยืนยันในไม่ช้า',
      errorMessage: 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้งในภายหลัง',
      errorSmtpAuth: 'การยืนยันตัวตนเซิร์ฟเวอร์อีเมลล้มเหลว กรุณาติดต่อเราโดยตรง',
      errorSmtpConnect: 'การเชื่อมต่อเซิร์ฟเวอร์อีเมลล้มเหลว กรุณาลองใหม่อีกครั้ง',
      errorSmtpTimeout: 'เซิร์ฟเวอร์อีเมลหมดเวลา กรุณาลองใหม่อีกครั้ง',
      errorInvalidEmail: 'ที่อยู่อีเมลที่กรอกไม่ถูกต้อง',
      errorEmailConfig: 'ไม่มีการกำหนดค่าอีเมล กรุณาติดต่อเราโดยตรงที่ service@villa-thailand.com',
      namePlaceholder: 'ชื่อเต็มของคุณ',
      emailPlaceholder: 'your@email.com',
      phonePlaceholder: 'หมายเลขโทรศัพท์ของคุณ'
    }
  };

  const txt = texts[language] || texts.de;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    // Validate total guests
    const totalGuests = parseInt(formData.adults) + parseInt(formData.children);
    if (totalGuests > 6) {
      setStatus({ type: 'error', message: txt.maxGuests });
      setIsSubmitting(false);
      return;
    }

    // Validate minimum stay of 3 nights
    if (nights < 3) {
      setStatus({ type: 'error', message: txt.minStayError });
      setIsSubmitting(false);
      return;
    }

    // Validate price proposal is entered
    if (!enteredCurrency || (!formData.proposedPriceEUR && !formData.proposedPriceUSD && !formData.proposedPriceTHB)) {
      setStatus({ type: 'error', message: txt.priceRequired });
      setIsSubmitting(false);
      return;
    }

    try {
      // Get the correct price based on entered currency
      let priceDisplay = '';
      if (enteredCurrency === 'EUR' && formData.proposedPriceEUR) {
        priceDisplay = `${formData.proposedPriceEUR} EUR`;
      } else if (enteredCurrency === 'USD' && formData.proposedPriceUSD) {
        priceDisplay = `${formData.proposedPriceUSD} USD`;
      } else if (enteredCurrency === 'THB' && formData.proposedPriceTHB) {
        priceDisplay = `${formData.proposedPriceTHB} THB`;
      } else if (formData.proposedPriceEUR) {
        priceDisplay = `${formData.proposedPriceEUR} EUR`;
      }

      // Transfer text
      const transferTexts = {
        none: 'Kein Transfer',
        bkk4: 'Bangkok (BKK) - bis 4 Personen',
        bkk6: 'Bangkok (BKK) - bis 6 Personen',
        dmk4: 'Don Mueang (DMK) - bis 4 Personen',
        dmk6: 'Don Mueang (DMK) - bis 6 Personen',
        huahin4: 'Hua Hin - bis 4 Personen',
        huahin6: 'Hua Hin - bis 6 Personen'
      };

      // Using Formspree for static website compatibility
      const response = await fetch('https://formspree.io/f/xnjjbzzg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          _subject: `Buchungsanfrage: ${formData.checkIn} bis ${formData.checkOut}`,
          name: formData.name,
          email: formData.email,
          phone: formData.phone || '-',
          country: formData.country || '-',
          checkIn: formData.checkIn,
          checkOut: formData.checkOut,
          nights: nights,
          adults: formData.adults,
          children: formData.children,
          totalGuests: totalGuests,
          transfer: transferTexts[formData.airportTransfer] || formData.airportTransfer,
          price: priceDisplay || '-',
          message: formData.message || '-'
        }),
      });

      if (response.ok) {
        setStatus({ type: 'success', message: txt.successMessage });
        setFormData({
          name: '',
          email: '',
          phone: '',
          country: '',
          checkIn: '',
          checkOut: '',
          adults: 2,
          children: 0,
          airportTransfer: 'none',
          proposedPriceEUR: '',
          proposedPriceUSD: '',
          proposedPriceTHB: '',
          message: ''
        });
        setEnteredCurrency(null);
      } else {
        setStatus({ type: 'error', message: txt.errorMessage });
      }
    } catch (error) {
      // Network error or connection refused
      setStatus({ type: 'error', message: `${txt.errorMessage} (Netzwerkfehler: ${error.message || 'Verbindung fehlgeschlagen'})` });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get minimum date (today)
  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
      <Header />

      <main className="container mx-auto px-4 py-12 max-w-4xl pt-28">
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">{txt.title}</h1>
          <p className="text-lg text-amber-600 font-medium">{txt.subtitle}</p>
        </div>

        {/* Info Box */}
        <Card className="p-4 mb-8 bg-blue-50 border-blue-200">
          <div className="flex gap-3">
            <Info className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
            <p className="text-blue-800 text-sm md:text-base">{txt.infoText}</p>
          </div>
        </Card>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Travel Dates */}
          <Card className="p-6 border-amber-200">
            <div className="flex items-center gap-2 mb-4">
              <Calendar className="w-5 h-5 text-amber-600" />
              <h2 className="text-xl font-semibold text-gray-800">{txt.travelDates}</h2>
              <span className="text-sm text-gray-500">{txt.minStay}</span>
            </div>
            
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{txt.checkIn} *</label>
                <input
                  type="date"
                  name="checkIn"
                  value={formData.checkIn}
                  onChange={handleChange}
                  min={today}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{txt.checkOut} *</label>
                <input
                  type="date"
                  name="checkOut"
                  value={formData.checkOut}
                  onChange={handleChange}
                  min={formData.checkIn || today}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{txt.nights}</label>
                <div className={`w-full px-4 py-3 border rounded-lg text-center font-semibold text-lg ${nights > 0 && nights < 3 ? 'bg-red-50 border-red-300 text-red-700' : 'bg-amber-50 border-amber-200 text-amber-700'}`}>
                  {nights}
                </div>
                {nights > 0 && nights < 3 && (
                  <p className="text-red-600 text-sm mt-1">{txt.minStayError}</p>
                )}
              </div>
            </div>
          </Card>

          {/* Guests */}
          <Card className="p-6 border-amber-200">
            <div className="flex items-center gap-2 mb-4">
              <Users className="w-5 h-5 text-amber-600" />
              <h2 className="text-xl font-semibold text-gray-800">{txt.guests}</h2>
              <span className="text-sm text-gray-500">({txt.maxGuests})</span>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{txt.adults} *</label>
                <select
                  name="adults"
                  value={formData.adults}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                >
                  {[1, 2, 3, 4, 5, 6].map(num => (
                    <option key={num} value={num}>{num}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{txt.children}</label>
                <select
                  name="children"
                  value={formData.children}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                >
                  {[0, 1, 2, 3, 4, 5].map(num => (
                    <option key={num} value={num}>{num}</option>
                  ))}
                </select>
              </div>
            </div>
          </Card>

          {/* Contact Data */}
          <Card className="p-6 border-amber-200">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">{txt.contactData}</h2>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{txt.name} *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  placeholder={txt.namePlaceholder}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{txt.email} *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  placeholder={txt.emailPlaceholder}
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{txt.phone}</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  placeholder={txt.phonePlaceholder}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{txt.country}</label>
                <input
                  type="text"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  placeholder={txt.countryPlaceholder}
                />
              </div>
            </div>
          </Card>

          {/* Price Proposal */}
          <Card className="p-6 border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50">
            <div className="flex items-center gap-2 mb-2">
              <Euro className="w-5 h-5 text-amber-600" />
              <h2 className="text-xl font-semibold text-gray-800">{txt.priceProposal}</h2>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              {txt.priceInfo} <strong>{txt.priceInfoBold}</strong> {txt.priceInfoEnd}
            </p>
            
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">EUR</label>
                <input
                  type="number"
                  value={enteredCurrency === 'EUR' ? formData.proposedPriceEUR : ''}
                  onChange={(e) => handlePriceChange('EUR', e.target.value)}
                  min="0"
                  step="10"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-lg"
                  placeholder="z.B. 1200"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">USD</label>
                <input
                  type="number"
                  value={enteredCurrency === 'USD' ? formData.proposedPriceUSD : ''}
                  onChange={(e) => handlePriceChange('USD', e.target.value)}
                  min="0"
                  step="10"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-lg"
                  placeholder="z.B. 1400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">THB</label>
                <input
                  type="number"
                  value={enteredCurrency === 'THB' ? formData.proposedPriceTHB : ''}
                  onChange={(e) => handlePriceChange('THB', e.target.value)}
                  min="0"
                  step="100"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-lg"
                  placeholder="z.B. 44000"
                />
              </div>
            </div>
            
            {/* Price conversion display */}
            {enteredCurrency && formData.proposedPriceEUR && (
              <div className="mt-4 p-3 bg-white rounded-lg border border-amber-200">
                <p className="text-gray-700">
                  <span className="font-semibold">
                    {enteredCurrency === 'EUR' && `${formData.proposedPriceEUR} € (EUR)`}
                    {enteredCurrency === 'USD' && `${formData.proposedPriceUSD} $ (USD)`}
                    {enteredCurrency === 'THB' && `${formData.proposedPriceTHB} ฿ (THB)`}
                  </span>
                  {' '}{txt.correspondsTo}{' '}
                  {enteredCurrency !== 'EUR' && <span>{formData.proposedPriceEUR} € (EUR)</span>}
                  {enteredCurrency !== 'USD' && <>{enteredCurrency !== 'EUR' && ', '}<span>{formData.proposedPriceUSD} $ (USD)</span></>}
                  {enteredCurrency !== 'THB' && <>, <span>{formData.proposedPriceTHB} ฿ (THB)</span></>}
                </p>
                <p className="text-xs text-gray-500 mt-1 italic">{txt.priceConversionNote}</p>
              </div>
            )}
            
            {nights > 0 && formData.proposedPriceEUR && (
              <p className="text-sm text-gray-500 mt-3 text-center">
                (~{(parseFloat(formData.proposedPriceEUR) / nights).toFixed(2)} EUR <strong>pro Nacht</strong>)
              </p>
            )}
          </Card>

          {/* Airport Transfer */}
          <Card className="p-6 border-amber-200">
            <div className="flex flex-col md:flex-row gap-6">
              {/* Transfer Options */}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Plane className="w-5 h-5 text-amber-600" />
                  <h2 className="text-xl font-semibold text-gray-800">{txt.airportTransfer}</h2>
                </div>
                <p className="text-sm text-gray-500 mb-4">{txt.transferInfo}</p>
                
                <div className="space-y-2">
                  <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="radio"
                      name="airportTransfer"
                      value="none"
                      checked={formData.airportTransfer === 'none'}
                      onChange={handleChange}
                      className="w-4 h-4 text-amber-600 focus:ring-amber-500"
                    />
                    <span>{txt.transferNo}</span>
                  </label>
                  <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="radio"
                      name="airportTransfer"
                      value="yes"
                      checked={formData.airportTransfer === 'yes'}
                      onChange={handleChange}
                      className="w-4 h-4 text-amber-600 focus:ring-amber-500"
                    />
                    <span>
                      {txt.transferYes}
                      {" "}
                      <span className="text-gray-500">
                        ({parseInt(formData.adults) + parseInt(formData.children) <= 4 
                          ? '~2.500 THB / ~65 €' 
                          : '~3.500 THB / ~90 €'})
                      </span>
                    </span>
                  </label>
                </div>
              </div>
              
              {/* Transfer Image */}
              <div className="hidden md:flex flex-shrink-0 items-stretch">
                <img
                  src="https://customer-assets.emergentagent.com/job_hua-hin-villa/artifacts/txne1dn4_Flughafen.jpg"
                  alt="Flughafentransfer"
                  className="h-full w-auto max-w-xs rounded-lg object-cover"
                />
              </div>
            </div>
          </Card>

          {/* Additional Message */}
          <Card className="p-6 border-amber-200">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">{txt.additionalMessage}</h2>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows={4}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 resize-none"
              placeholder={txt.messagePlaceholder}
            />
          </Card>

          {/* Status Message */}
          {status.message && (
            <div className={`flex items-center gap-2 p-4 rounded-lg ${
              status.type === 'success' 
                ? 'bg-green-50 text-green-800 border border-green-200' 
                : 'bg-red-50 text-red-800 border border-red-200'
            }`}>
              {status.type === 'success' ? (
                <CheckCircle className="w-5 h-5 flex-shrink-0" />
              ) : (
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
              )}
              <span>{status.message}</span>
            </div>
          )}

          {/* Submit Button */}
          <div className="text-center pt-4">
            <Button 
              type="submit"
              disabled={isSubmitting}
              size="lg"
              className="bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white px-8 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed text-lg"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  {txt.submitting}
                </>
              ) : (
                <>
                  <Send className="w-5 h-5 mr-2" />
                  {txt.submitButton}
                </>
              )}
            </Button>
          </div>
        </form>
      </main>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-8 mt-16">
        <div className="container mx-auto px-4 text-center">
          <p className="text-gray-400">
            {t('footer.copyright')}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default BookingRequest;
